import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWishlist,
  removeWishItem,
} from "../../redux/features/getWishlistSlice";
import { addToCart, updateCart } from "../../redux/features/cartSlice";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

function Wishlist() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { likedItems, loading, error } = useSelector(
    (state) => state.wishlist
  );

  useEffect(() => {
    dispatch(fetchWishlist());
  }, [dispatch]);

  const handleAddToCart = async (productId) => {
    try {
      await dispatch(addToCart({ product_id: productId })).unwrap();
      toast.success("Added to cart", {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch (e) {
      toast.error("Failed to add", {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    }
  };

  const handleUpdateCart = async (product_id, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await dispatch(updateCart({ product_id, quantity: newQuantity })).unwrap();
      toast.success("Quantity updated", {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch {
      toast.error(t("Error_updating_cart"), {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    }
  };

  const handleRemove = async (id) => {
    try {
      await dispatch(removeWishItem(id)).unwrap();
      toast.success(t("Removed_from_your_wishlist"), {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    } catch {
      toast.error(t("Failed_to_remove_product_from_wishlist"), {
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

      });
    }
  };

  return (
    <section className="main-bg pt-3 pb-5">
      <div className="container px-lg-5">
        <h2 className="text-center mb-3 fw-bold text-green-theme">
          {t("My_Wishlist")}
        </h2>

        {/* Loading */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-green-theme"></div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center text-danger">
            {typeof error === "string" ? error : error?.message}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && likedItems.length === 0 && (
          <div className="text-center text-muted py-5 fs-5">
            {t("No_items_in_your_wishlist")}
          </div>
        )}

        {/* Wishlist Grid */}
        {!loading && !error && likedItems.length > 0 && (
          <div className="row g-4">
            {likedItems.map((product) => (
              <div key={product.id} className="col-12 col-sm-6 col-md-4">
                <div className="wishlist-card shadow rounded-4 border-0 p-3 h-100 d-flex flex-column position-relative bg-white modern-wishlist-card">

                  {/* Remove Icon */}
                  <button
                    className="btn btn-light border-0 shadow-sm position-absolute top-0 end-0 m-2 rounded-circle"
                    onClick={() => handleRemove(product.id)}
                    data-bs-toggle="tooltip"
                    data-bs-placement="left"
                    title={t("Remove_from_wishlist")}
                    style={{ padding: "7px 10px" }}
                  >
                    <i className="fa-solid fa-heart fa-lg text-danger"></i>
                  </button>

                  {/* Product Image */}
                  <Link
                    to={`/product/${product.id}`}
                  >
                    <div className="wishlist-img-wrapper d-flex justify-content-center rounded-4 overflow-hidden">
                      <img
                        src={
                          Array.isArray(product.image) && product.image.length > 0
                            ? product.image[0]
                            : product.image || "https://via.placeholder.com/240"
                        }
                        alt="No Image"
                        onError={(e) => {
                          e.target.onerror = null; // prevents infinite loop
                          e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                        }}
                        className="img-fluid wishlist-img"
                      />
                    </div>
                  </Link>

                  {/* Content */}
                  <Link
                    to={`/product/${product.id}`}

                  >
                    <div className="mt-3 text-start flex-grow-1">
                      <h5 className="fw-semibold text-green-theme mb-2 modern-title">
                        {product.title}
                      </h5>

                      <p className="text-dark fw-bold fs-6 mb-3 modern-price">
                        €{product.sales_price}
                      </p>
                    </div>
                  </Link>

                  {/* Buttons Row */}
                  <div className="d-flex gap-2 mt-auto">

                    {/* View Details */}
                    {/* <Link
                      to={`/product/${product.id}`}
                      className="btn btn-outline-secondary w-50 rounded-pill fw-semibold"
                    >
                      {t("View_Product")}
                    </Link> */}

                    {/* Add to Cart / Add More */}
                    {product.in_cart ? (
                      <button
                        className="btn btn-stylish w-50 rounded-pill fw-semibold"
                        onClick={() =>
                          handleUpdateCart(product.id, product.quantity + 1)
                        }
                      >
                        <i className="fa fa-plus me-1"></i> {t("add_more")}
                      </button>
                    ) : (
                      <button
                        className="btn btn-stylish w-50 rounded-pill fw-semibold"
                        onClick={() => handleAddToCart(product.id)}
                      >
                        <i className="fa fa-shopping-cart me-1"></i>
                        {t("add_to_cart")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extra Styling */}
      <style>{`
        .modern-wishlist-card {
          transition: all 0.25s ease;
        }
        .modern-wishlist-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
        }
        .modern-title {
          font-size: 1rem;
        }
        .modern-price {
          color: var(--secondary-light-color);
        }
        .wishlist-img-wrapper {
          height: 180px;
          
        }
        .wishlist-img-wrapper img {
          height: 100%;
          object-fit: cover;
        }
      `}</style>
    </section>
  );
}

export default Wishlist;
